// ============================================================
//  storage.js - Universal Storage for RAATH DINE (Electron + Browser)
//  Electron: SQLite via IPC (dbQuery) | Web Demo: IndexedDB
//  Cloud Sync: Browser mode enabled, Electron disabled
// ============================================================

// ✅ FIX: LAZY LOAD cloudSync to avoid circular dependency
let cloudSyncInstance = null;

async function getCloudSync() {
  if (!cloudSyncInstance) {
    try {
      const module = await import('../cloudSync');
      cloudSyncInstance = module.cloudSync || module.default;
    } catch (e) {
      console.warn('[Storage] Cloud sync not available:', e);
    }
  }
  return cloudSyncInstance;
}

const isElectron = typeof window !== 'undefined' && !!window.electronAPI;

// ==================== SYNC CONTROLLER ====================
const SYNC_ENABLED = !isElectron;

console.log(`[Storage] Mode: ${isElectron ? 'ELECTRON' : 'BROWSER'}`);
console.log(`[Storage] Cloud Sync: ${SYNC_ENABLED ? 'ENABLED ✅' : 'DISABLED ❌'}`);

// ✅ FIX: Get shopId from localStorage (set by AuthContext/Signup/Login)
function getShopId() {
  const shopId = localStorage.getItem('raath_shop_id');
  if (shopId && shopId !== 'shop_default') {
    return shopId;
  }
  // Fallback: generate new shopId if missing
  const newShopId = `shop_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  localStorage.setItem('raath_shop_id', newShopId);
  console.warn(`[Storage] Generated fallback shopId: ${newShopId}`);
  return newShopId;
}

// ==================== SMART SYNC WRAPPER ====================
async function syncToCloud(collection, data, operation = 'push', shopId = null) {
  if (!SYNC_ENABLED) return;
  if (!data || !data.id) {
    console.warn(`[Storage] Sync skipped - no id in data`);
    return;
  }
  try {
    const cloudSync = await getCloudSync();
    if (cloudSync) {
      const effectiveShopId = shopId || getShopId();
      if (operation === 'push') {
        await cloudSync.push(collection, data, effectiveShopId);
      } else if (operation === 'remove') {
        await cloudSync.remove(collection, data.id || data, effectiveShopId);
      }
    }
  } catch (e) {
    console.error(`[Storage] Cloud sync failed for ${collection}:`, e);
  }
}

// ==================== INDEXEDDB SETUP ====================
const DB_NAME = 'RAATH_DINE_DEMO';
const DB_VERSION = 2;

const STORES = [
  'tables', 'menu_categories', 'menu_items', 'modifiers',
  'orders', 'order_items', 'kot_log',
  'inventory', 'recipes', 'expenses',
  'staff', 'attendance', 'salary_records', 'loans',
  'settings', 'users', 'roles', 'deals'
];

let idbPromise = null;
let defaultDataSeeded = false;

function initIndexedDB() {
  if (idbPromise) return idbPromise;
  idbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      STORES.forEach(storeName => {
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName, { keyPath: 'id', autoIncrement: false });
          console.log(`[IndexedDB] Created store: ${storeName}`);
        }
      });
    };
  });
  return idbPromise;
}

async function ensureIndexedDB() {
  try {
    const db = await initIndexedDB();
    const missingStores = STORES.filter(s => !db.objectStoreNames.contains(s));
    if (missingStores.length > 0) {
      console.warn('[IndexedDB] Missing stores:', missingStores);
      db.close();
      await new Promise((resolve, reject) => {
        const req = indexedDB.deleteDatabase(DB_NAME);
        req.onsuccess = resolve;
        req.onerror = reject;
      });
      idbPromise = null;
      await initIndexedDB();
      console.log('[IndexedDB] Database recreated with all stores');
    }
    return db;
  } catch (e) {
    console.error('[IndexedDB] Failed to ensure DB:', e);
    throw e;
  }
}

async function idbGetAll(storeName) {
  await ensureIndexedDB();
  const db = await initIndexedDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const r = store.getAll();
    r.onsuccess = () => resolve(r.result || []);
    r.onerror = () => reject(r.error);
  });
}

async function idbGetById(storeName, id) {
  if (id === undefined || id === null || id === '') {
    console.warn(`[Storage] idbGetById called with invalid ID for store: ${storeName}`, id);
    return null;
  }
  
  await ensureIndexedDB();
  const db = await initIndexedDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    let found = false;
    const check = (result) => {
      if (!found && result !== undefined) {
        found = true;
        resolve(result);
      }
    };
    const r1 = store.get(id);
    r1.onsuccess = () => check(r1.result);
    const r2 = store.get(String(id));
    r2.onsuccess = () => check(r2.result);
    const numId = Number(id);
    if (!isNaN(numId)) {
      const r3 = store.get(numId);
      r3.onsuccess = () => check(r3.result);
    }
    tx.oncomplete = () => { if (!found) resolve(null); };
    tx.onerror = () => reject(tx.error);
  });
}

async function idbAdd(storeName, data) {
  await ensureIndexedDB();
  const db = await initIndexedDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const cleanData = { ...data };
    if (cleanData.id === undefined || cleanData.id === null) {
      cleanData.id = String(Date.now() + Math.floor(Math.random() * 10000));
    } else {
      cleanData.id = String(cleanData.id);
    }
    const r = store.add(cleanData);
    r.onsuccess = () => {
      syncToCloud(storeName, cleanData, 'push', getShopId()).catch(console.error);
      resolve({ lastInsertRowid: cleanData.id, changes: 1 });
    };
    r.onerror = () => reject(r.error);
  });
}

async function idbPut(storeName, data) {
  await ensureIndexedDB();
  if (!data || data.id === undefined || data.id === null) {
    return Promise.reject(new Error(`Cannot put into ${storeName}: object is missing 'id' property.`));
  }
  const db = await initIndexedDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const r = store.put(data);
    r.onsuccess = () => {
      syncToCloud(storeName, data, 'push', getShopId()).catch(console.error);
      resolve({ lastInsertRowid: data.id, changes: 1 });
    };
    r.onerror = () => reject(r.error);
  });
}

async function idbDelete(storeName, id) {
  await ensureIndexedDB();
  const record = await idbGetById(storeName, id);
  if (!record) return { changes: 0 };
  const db = await initIndexedDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const r = store.delete(record.id);
    r.onsuccess = () => {
      syncToCloud(storeName, record.id, 'remove', getShopId()).catch(console.error);
      resolve({ changes: 1 });
    };
    r.onerror = () => reject(r.error);
  });
}

// ==================== HELPERS ====================
function safeStringify(data) {
  if (typeof data === 'string') return data;
  try { return JSON.stringify(data || []); } catch (e) { return '[]'; }
}

function safeParse(json, fallback = []) {
  if (typeof json !== 'string') return json || fallback;
  try { 
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch (e) { 
    return fallback; 
  }
}

function toIsoDate(d) {
  if (!d) return new Date().toISOString();
  if (typeof d === 'string') return new Date(d).toISOString();
  return d.toISOString();
}

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

function monthStartStr() {
  return todayStr().substring(0, 7) + '-01';
}

// ==================== STORAGE CLASS ====================
class Storage {
  constructor() {
    this.mode = this.detectMode();
    this.syncEnabled = SYNC_ENABLED;
    this._dbMutex = Promise.resolve();
    this._kotCounter = 0;
    
    console.log(`[Storage] Initialized - Mode: ${this.mode}, Sync: ${this.syncEnabled ? 'ON ✅' : 'OFF ❌'}`);
  }

  detectMode() {
    return (typeof window !== 'undefined' && window.electronAPI && window.electronAPI.isElectron) ? 'electron' : 'browser';
  }

  async _withMutex(fn) {
    const oldMutex = this._dbMutex;
    let release;
    const newMutex = new Promise(resolve => { release = resolve; });
    this._dbMutex = oldMutex.then(() => newMutex).catch(() => newMutex);
    await oldMutex.catch(() => {});
    try { return await fn(); } finally { release(); }
  }

  _sleep(ms) { return new Promise(resolve => setTimeout(resolve, ms)); }

  async electronQuery(sql, params = [], retries = 5) {
    return this._withMutex(async () => {
      let lastError;
      for (let i = 0; i < retries; i++) {
        try {
          if (!window.electronAPI || !window.electronAPI.dbQuery) {
            throw new Error('Electron API not available');
          }
          return await window.electronAPI.dbQuery(sql, params);
        } catch (error) {
          lastError = error;
          if (error.message && error.message.includes('SQLITE_BUSY')) {
            await this._sleep(50 * Math.pow(2, i) + Math.random() * 50);
            continue;
          }
          throw error;
        }
      }
      throw lastError;
    });
  }

  // ==================== INIT & SEED (Browser Only) ====================
  async init() {
    if (this.mode !== 'browser' || defaultDataSeeded) return;
    await ensureIndexedDB();

    const settings = await idbGetById('settings', '1');
    if (!settings) {
      const settingsData = {
        id: '1',
        restaurant_name: 'My Restaurant',
        address: '',
        phone: '',
        tax_percent: 0,
        service_charge_percent: 0,
        currency_symbol: 'PKR',
        printer_name: '',
        kitchen_printer: '',
        bill_header: '',
        bill_footer: 'Thank you for dining with us!',
        theme_color: '#1976d2',
        created_at: new Date().toISOString()
      };
      await idbAdd('settings', settingsData);
    }

    const cats = await idbGetAll('menu_categories');
    if (cats.length === 0) {
      const defaultCategories = [
        { name: 'Fast Food', name_urdu: 'فاسٹ فوڈ', type: 'fast_food', color: '#ff5722' },
        { name: 'Desi Food', name_urdu: 'دیسی کھانا', type: 'desi', color: '#4caf50' },
        { name: 'BBQ', name_urdu: 'باربی کیو', type: 'bbq', color: '#795548' },
        { name: 'Drinks', name_urdu: 'مشروبات', type: 'drinks', color: '#2196f3' },
        { name: 'Karahi', name_urdu: 'کڑاہی', type: 'desi', color: '#ff9800' },
      ];
      for (const cat of defaultCategories) {
        await idbAdd('menu_categories', { ...cat, sort_order: 0, is_active: 1, created_at: new Date().toISOString() });
      }
    }

    const tables = await idbGetAll('tables');
    if (tables.length === 0) {
      for (let i = 1; i <= 10; i++) {
        await idbAdd('tables', {
          table_number: `T-${i}`,
          floor: 'Ground Floor',
          capacity: 4,
          status: 'empty',
          current_order_id: null,
          merged_with: null,
          position_x: 0,
          position_y: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
      }
    }

    const roles = await idbGetAll('roles');
    if (roles.length === 0) {
      const defaultRoles = [
        { id: 'admin', label: 'Admin', color: '#d32f2f', pages: ['dashboard','pos','tables','menu','orders','kitchen','inventory','accounts','staff','reports','settings','backup'], permissions: ['create_order','edit_order','delete_order','manage_menu','manage_inventory','manage_staff','view_reports','manage_settings','manage_backup','cancel_bill','apply_discount','split_bill','merge_tables','refund','view_accounts','manage_expenses'], is_deleted: 0 },
        { id: 'manager', label: 'Manager', color: '#1976d2', pages: ['dashboard','pos','tables','menu','orders','kitchen','inventory','accounts','staff','reports','settings'], permissions: ['create_order','edit_order','manage_menu','manage_inventory','manage_staff','view_reports','apply_discount','split_bill','merge_tables','refund','view_accounts','manage_expenses','cancel_bill'], is_deleted: 0 },
        { id: 'cashier', label: 'Cashier', color: '#388e3c', pages: ['pos','tables','orders','kitchen'], permissions: ['create_order','edit_order','apply_discount','split_bill','print_bill','print_kot'], is_deleted: 0 },
        { id: 'waiter', label: 'Waiter', color: '#f57c00', pages: ['pos','tables','orders','kitchen'], permissions: ['create_order','edit_order','print_kot'], is_deleted: 0 },
        { id: 'kitchen', label: 'Kitchen Staff', color: '#7b1fa2', pages: ['kitchen'], permissions: ['update_kitchen_status','view_kot'], is_deleted: 0 }
      ];
      for (const role of defaultRoles) {
        await idbAdd('roles', { 
          ...role, 
          pages: safeStringify(role.pages), 
          permissions: safeStringify(role.permissions), 
          created_at: new Date().toISOString() 
        });
      }
      console.log('✅ Default roles seeded');
    }

    const users = await idbGetAll('users');
    const hasAdmin = users.some(u => u.email === 'admin@raath.com' && !u.is_deleted);
    if (!hasAdmin) {
      await idbAdd('users', {
        name: 'Admin',
        email: 'admin@raath.com',
        phone: '',
        password: 'admin123',
        password_hash: 'admin123',
        role: 'admin',
        shop_name: '',
        shop_address: '',
        business_type: 'restaurant',
        currency: 'PKR',
        status: 'active',
        is_deleted: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });
      console.log('✅ Default admin user created');
    }

    defaultDataSeeded = true;
    console.log('✅ RAATH DINE Browser DB seeded');
  }

  // ==================== TABLE FUNCTIONS ====================
  async getTables() {
    if (this.mode === 'electron') {
      return this.electronQuery('SELECT * FROM tables ORDER BY floor, table_number');
    }
    await this.init();
    const rows = await idbGetAll('tables');
    return rows.sort((a, b) => (a.floor || '').localeCompare(b.floor || '') || (a.table_number || '').localeCompare(b.table_number || ''));
  }

  async addTable(table) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery('INSERT INTO tables (table_number, floor, capacity, position_x, position_y) VALUES (?, ?, ?, ?, ?)', [table.table_number, table.floor, table.capacity, table.position_x || 0, table.position_y || 0]);
      if (SYNC_ENABLED) {
        syncToCloud('tables', { ...table, id: result.lastInsertRowid, status: 'empty', current_order_id: null, merged_with: null, created_at: new Date().toISOString(), updated_at: new Date().toISOString() }, 'push', getShopId());
      }
      return result;
    }
    await this.init();
    const tableData = {
      ...table,
      status: 'empty',
      current_order_id: null,
      merged_with: null,
      position_x: table.position_x || 0,
      position_y: table.position_y || 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    return idbAdd('tables', tableData);
  }

  async updateTableStatus(id, newStatus) {
    if (this.mode === 'electron') {
      const stmt = `UPDATE tables SET status = ?, updated_at = datetime('now'), current_order_id = CASE WHEN ? = 'empty' OR ? = 'cleaning' THEN NULL ELSE current_order_id END WHERE id = ?`;
      const result = await this.electronQuery(stmt, [newStatus, newStatus, newStatus, id]);
      if (SYNC_ENABLED) {
        syncToCloud('tables', { id, status: newStatus, updated_at: new Date().toISOString() }, 'push', getShopId());
      }
      return result;
    }
    await this.init();
    const table = await idbGetById('tables', id);
    if (!table) throw new Error('Table not found!');
    table.status = newStatus;
    table.updated_at = new Date().toISOString();
    if (newStatus === 'empty' || newStatus === 'cleaning') {
      table.current_order_id = null;
    }
    await idbPut('tables', table);
    return { success: true, tableId: id, newStatus };
  }

  async deleteTable(id) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery('DELETE FROM tables WHERE id = ?', [id]);
      if (SYNC_ENABLED) {
        syncToCloud('tables', id, 'remove', getShopId());
      }
      return result;
    }
    return idbDelete('tables', id);
  }

  async mergeTables(tableIds) {
    if (this.mode === 'electron') {
      const mainTable = tableIds[0];
      const mergedTables = tableIds.slice(1);
      await this.electronQuery('UPDATE tables SET merged_with = ?, status = ? WHERE id = ?', [safeStringify(mergedTables), 'occupied', mainTable]);
      if (SYNC_ENABLED) {
        syncToCloud('tables', { id: mainTable, merged_with: safeStringify(mergedTables), status: 'occupied' }, 'push', getShopId());
      }
      for (const id of mergedTables) {
        await this.electronQuery('UPDATE tables SET status = ? WHERE id = ?', ['merged', id]);
        if (SYNC_ENABLED) {
          syncToCloud('tables', { id, status: 'merged' }, 'push', getShopId());
        }
      }
      return { success: true };
    }
    const mainTable = tableIds[0];
    const mergedTables = tableIds.slice(1);
    const main = await idbGetById('tables', mainTable);
    if (main) { main.merged_with = safeStringify(mergedTables); main.status = 'occupied'; main.updated_at = new Date().toISOString(); await idbPut('tables', main); }
    for (const id of mergedTables) {
      const t = await idbGetById('tables', id);
      if (t) { t.status = 'merged'; t.updated_at = new Date().toISOString(); await idbPut('tables', t); }
    }
    return { success: true };
  }

  // ==================== MENU FUNCTIONS ====================
  async getCategories() {
    if (this.mode === 'electron') {
      return this.electronQuery('SELECT * FROM menu_categories WHERE is_active = 1 ORDER BY sort_order');
    }
    await this.init();
    const rows = await idbGetAll('menu_categories');
    return rows.filter(x => x.is_active !== 0).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }

  async addCategory(category) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery('INSERT INTO menu_categories (name, name_urdu, type, color, sort_order) VALUES (?, ?, ?, ?, ?)', [category.name, category.name_urdu, category.type, category.color, category.sort_order || 0]);
      if (SYNC_ENABLED) {
        syncToCloud('menu_categories', { ...category, id: result.lastInsertRowid, is_active: 1, sort_order: category.sort_order || 0, created_at: new Date().toISOString() }, 'push', getShopId());
      }
      return result;
    }
    await this.init();
    return idbAdd('menu_categories', { ...category, sort_order: category.sort_order || 0, is_active: 1, created_at: new Date().toISOString() });
  }

  async deleteCategory(id) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery('DELETE FROM menu_categories WHERE id = ?', [id]);
      if (SYNC_ENABLED) {
        syncToCloud('menu_categories', id, 'remove', getShopId());
      }
      return result;
    }
    return idbDelete('menu_categories', id);
  }

  async getMenuItems() {
    if (this.mode === 'electron') {
      const items = await this.electronQuery(`SELECT mi.*, mc.name as category_name, mc.color as category_color FROM menu_items mi LEFT JOIN menu_categories mc ON mi.category_id = mc.id WHERE mi.is_active = 1 ORDER BY mc.sort_order, mi.sort_order`);
      for (const item of items) {
        item.modifiers = await this.electronQuery('SELECT * FROM modifiers WHERE item_id = ? AND is_active = 1', [item.id]);
      }
      return items;
    }
    await this.init();
    const [items, categories] = await Promise.all([idbGetAll('menu_items'), idbGetAll('menu_categories')]);
    const active = items.filter(x => x.is_active !== 0);
    for (const item of active) {
      const mods = await idbGetAll('modifiers');
      item.modifiers = mods.filter(m => String(m.item_id) === String(item.id) && m.is_active !== 0);
      const cat = categories.find(c => String(c.id) === String(item.category_id));
      item.category_name = cat?.name || '';
      item.category_color = cat?.color || '';
    }
    return active.sort((a, b) => ((a.sort_order || 0) - (b.sort_order || 0)));
  }

  async getMenuItemsByCategory(categoryId) {
    if (this.mode === 'electron') {
      return this.electronQuery('SELECT * FROM menu_items WHERE category_id = ? AND is_active = 1', [categoryId]);
    }
    await this.init();
    const items = await idbGetAll('menu_items');
    return items.filter(x => String(x.category_id) === String(categoryId) && x.is_active !== 0);
  }

  async addMenuItem(item) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery(`INSERT INTO menu_items (category_id, name, name_urdu, description, price, cost_price, kitchen_station, prep_time, is_veg, is_combo, combo_items, sort_order, variants, image) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [item.category_id, item.name, item.name_urdu, item.description, item.price, item.cost_price || 0, item.kitchen_station || 'main', item.prep_time || 15, item.is_veg || 0, item.is_combo || 0, safeStringify(item.combo_items), item.sort_order || 0, safeStringify(item.variants), item.image || null]);
      if (SYNC_ENABLED) {
        syncToCloud('menu_items', { ...item, id: result.lastInsertRowid, is_active: 1, cost_price: item.cost_price || 0, kitchen_station: item.kitchen_station || 'main', prep_time: item.prep_time || 15, is_veg: item.is_veg || 0, is_combo: item.is_combo || 0, combo_items: safeStringify(item.combo_items), sort_order: item.sort_order || 0, variants: safeStringify(item.variants), image: item.image || null, created_at: new Date().toISOString() }, 'push', getShopId());
      }
      return result;
    }
    await this.init();
    return idbAdd('menu_items', {
      ...item,
      cost_price: item.cost_price || 0,
      kitchen_station: item.kitchen_station || 'main',
      prep_time: item.prep_time || 15,
      is_veg: item.is_veg || 0,
      is_active: 1,
      is_combo: item.is_combo || 0,
      combo_items: safeStringify(item.combo_items),
      sort_order: item.sort_order || 0,
      variants: safeStringify(item.variants),
      image: item.image || null,
      created_at: new Date().toISOString()
    });
  }

  async updateMenuItem(id, item) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery(`UPDATE menu_items SET category_id = ?, name = ?, name_urdu = ?, description = ?, price = ?, cost_price = ?, kitchen_station = ?, prep_time = ?, is_veg = ?, is_active = ?, is_combo = ?, combo_items = ?, sort_order = ?, variants = ?, image = ? WHERE id = ?`,
        [item.category_id, item.name, item.name_urdu, item.description, item.price, item.cost_price, item.kitchen_station, item.prep_time, item.is_veg, item.is_active, item.is_combo, safeStringify(item.combo_items), item.sort_order, safeStringify(item.variants), item.image || null, id]);
      if (SYNC_ENABLED) {
        syncToCloud('menu_items', { ...item, id }, 'push', getShopId());
      }
      return result;
    }
    const e = await idbGetById('menu_items', id);
    if (!e) return { changes: 0 };
    return idbPut('menu_items', {
      ...e, ...item,
      combo_items: safeStringify(item.combo_items || e.combo_items),
      variants: safeStringify(item.variants || e.variants),
      id: e.id
    });
  }

  async deleteMenuItem(id) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery('UPDATE menu_items SET is_active = 0 WHERE id = ?', [id]);
      if (SYNC_ENABLED) {
        syncToCloud('menu_items', { id, is_active: 0 }, 'push', getShopId());
      }
      return result;
    }
    const e = await idbGetById('menu_items', id);
    if (!e) return { changes: 0 };
    e.is_active = 0;
    return idbPut('menu_items', e);
  }

  // ==================== MODIFIER FUNCTIONS ====================
  async getModifiers(itemId) {
    if (this.mode === 'electron') {
      return this.electronQuery('SELECT * FROM modifiers WHERE item_id = ? AND is_active = 1', [itemId]);
    }
    const mods = await idbGetAll('modifiers');
    return mods.filter(m => String(m.item_id) === String(itemId) && m.is_active !== 0);
  }

  async addModifier(modifier) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery('INSERT INTO modifiers (item_id, name, price, cost_price) VALUES (?, ?, ?, ?)', [modifier.item_id, modifier.name, modifier.price, modifier.cost_price || 0]);
      if (SYNC_ENABLED) {
        syncToCloud('modifiers', { ...modifier, id: result.lastInsertRowid, is_active: 1, cost_price: modifier.cost_price || 0 }, 'push', getShopId());
      }
      return result;
    }
    return idbAdd('modifiers', { ...modifier, cost_price: modifier.cost_price || 0, is_active: 1 });
  }

  async deleteModifier(id) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery('UPDATE modifiers SET is_active = 0 WHERE id = ?', [id]);
      if (SYNC_ENABLED) {
        syncToCloud('modifiers', { id, is_active: 0 }, 'push', getShopId());
      }
      return result;
    }
    const e = await idbGetById('modifiers', id);
    if (!e) return { changes: 0 };
    e.is_active = 0;
    return idbPut('modifiers', e);
  }

  async hardDeleteModifier(id) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery('DELETE FROM modifiers WHERE id = ?', [id]);
      if (SYNC_ENABLED) {
        syncToCloud('modifiers', id, 'remove', getShopId());
      }
      return result;
    }
    return idbDelete('modifiers', id);
  }

  async deleteModifiersByItem(itemId) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery('DELETE FROM modifiers WHERE item_id = ?', [itemId]);
      if (SYNC_ENABLED) {
        syncToCloud('modifiers', { item_id: itemId }, 'remove', getShopId());
      }
      return result;
    }
    const mods = await idbGetAll('modifiers');
    for (const m of mods.filter(x => String(x.item_id) === String(itemId))) {
      await idbDelete('modifiers', m.id);
    }
    return { changes: 1 };
  }

  async updateModifier(id, modifier) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery('UPDATE modifiers SET name = ?, price = ?, cost_price = ? WHERE id = ?', [modifier.name, modifier.price, modifier.cost_price || 0, id]);
      if (SYNC_ENABLED) {
        syncToCloud('modifiers', { ...modifier, id }, 'push', getShopId());
      }
      return result;
    }
    const e = await idbGetById('modifiers', id);
    if (!e) return { changes: 0 };
    return idbPut('modifiers', { ...e, ...modifier, cost_price: modifier.cost_price || 0, id: e.id });
  }

  // ==================== ORDER FUNCTIONS ====================
  _generateKOTNumber() {
    this._kotCounter++;
    const date = new Date().toISOString().split('T')[0].replace(/-/g, '');
    return parseInt(`${date}${String(this._kotCounter).padStart(4, '0')}`);
  }

  // ==================== CREATE ORDER (BROWSER + ELECTRON) ====================
  async createOrder(order) {
    if (this.mode === 'electron') {
      const insertOrder = await this.electronQuery(`INSERT INTO orders (table_id, order_type, customer_name, customer_phone, waiter_id, notes, discount_amount, discount_percent, service_charge) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [order.table_id, order.order_type, order.customer_name, order.customer_phone, order.waiter_id, order.notes, order.discount_amount || 0, order.discount_percent || 0, order.service_charge || 0]);
      const orderId = insertOrder.lastInsertRowid;
      if (SYNC_ENABLED) {
        syncToCloud('orders', { ...order, id: orderId, status: 'pending', total_amount: 0, final_amount: 0, created_at: new Date().toISOString(), updated_at: new Date().toISOString() }, 'push', getShopId());
      }
      let totalAmount = 0;
      for (const item of order.items) {
        const itemTotal = (item.unit_price * item.quantity) + (item.modifiers_price || 0);
        totalAmount += itemTotal;
        const menuItemId = item.menu_item_id && !String(item.menu_item_id).startsWith('deal_') ? item.menu_item_id : null;
        const itemResult = await this.electronQuery(`INSERT INTO order_items (order_id, menu_item_id, item_name, quantity, unit_price, modifiers, modifiers_price, notes, deal_id, is_deal, deal_name) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [orderId, menuItemId, item.item_name, item.quantity, item.unit_price, safeStringify(item.modifiers), item.modifiers_price || 0, item.notes, item.deal_id || null, item.is_deal ? 1 : 0, item.deal_name || null]);
        if (SYNC_ENABLED) {
          syncToCloud('order_items', { ...item, id: itemResult.lastInsertRowid, order_id: orderId, menu_item_id: menuItemId, modifiers: safeStringify(item.modifiers), modifiers_price: item.modifiers_price || 0, notes: item.notes || '', deal_id: item.deal_id || null, is_deal: item.is_deal ? 1 : 0, deal_name: item.deal_name || null, created_at: new Date().toISOString() }, 'push', getShopId());
        }
      }
      const discountAmount = order.discount_amount || 0;
      const serviceCharge = order.service_charge || 0;
      const finalTotal = Math.max(0, totalAmount + serviceCharge - discountAmount);
      await this.electronQuery('UPDATE orders SET total_amount = ?, final_amount = ? WHERE id = ?', [totalAmount, finalTotal, orderId]);
      if (order.table_id) {
        await this.electronQuery('UPDATE tables SET status = ?, current_order_id = ? WHERE id = ?', ['occupied', orderId, order.table_id]);
        if (SYNC_ENABLED) {
          syncToCloud('tables', { id: order.table_id, status: 'occupied', current_order_id: orderId }, 'push', getShopId());
        }
      }
      const kotNumber = this._generateKOTNumber();
      const kotResult = await this.electronQuery('INSERT INTO kot_log (order_id, kot_number, items, status) VALUES (?, ?, ?, ?)', [orderId, kotNumber, safeStringify(order.items), 'pending']);
      if (SYNC_ENABLED) {
        syncToCloud('kot_log', { order_id: orderId, kot_number: kotNumber, items: safeStringify(order.items), status: 'pending', id: kotResult.lastInsertRowid, created_at: new Date().toISOString() }, 'push', getShopId());
      }
      await this.electronQuery('UPDATE order_items SET kot_number = ? WHERE order_id = ?', [kotNumber, orderId]);
      return { orderId, kotNumber, totalAmount, finalAmount: finalTotal };
    }

    // ✅ BROWSER MODE
    await this.init();
    const orderResult = await idbAdd('orders', {
      table_id: order.table_id || null,
      order_type: order.order_type || 'dine_in',
      status: 'pending',
      customer_name: order.customer_name || '',
      customer_phone: order.customer_phone || '',
      waiter_id: order.waiter_id || null,
      total_amount: 0,
      tax_amount: 0,
      service_charge: order.service_charge || 0,
      discount_amount: order.discount_amount || 0,
      discount_percent: order.discount_percent || 0,
      discount_reason: '',
      tip_amount: 0,
      final_amount: 0,
      payment_method: 'cash',
      payment_status: 'pending',
      split_bill_data: null,
      notes: order.notes || '',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      billed_at: null
    });
    const orderId = orderResult.lastInsertRowid;
    let totalAmount = 0;
    for (const item of order.items) {
      const itemTotal = (item.unit_price * item.quantity) + (item.modifiers_price || 0);
      totalAmount += itemTotal;
      const menuItemId = item.menu_item_id && !String(item.menu_item_id).startsWith('deal_') ? item.menu_item_id : null;
      await idbAdd('order_items', {
        order_id: orderId,
        menu_item_id: menuItemId,
        item_name: item.item_name,
        quantity: item.quantity,
        unit_price: item.unit_price,
        modifiers: safeStringify(item.modifiers),
        modifiers_price: item.modifiers_price || 0,
        kitchen_status: 'pending',
        kot_number: null,
        kot_printed: 0,
        notes: item.notes || '',
        deal_id: item.deal_id || null,
        is_deal: item.is_deal ? 1 : 0,
        deal_name: item.deal_name || null,
        created_at: new Date().toISOString()
      });
    }
    const discountAmount = order.discount_amount || 0;
    const serviceCharge = order.service_charge || 0;
    const finalTotal = Math.max(0, totalAmount + serviceCharge - discountAmount);
    const o = await idbGetById('orders', orderId);
    o.total_amount = totalAmount;
    o.final_amount = finalTotal;
    await idbPut('orders', o);
    
    if (order.table_id) {
      const t = await idbGetById('tables', order.table_id);
      if (t) { 
        t.status = 'occupied'; 
        t.current_order_id = orderId; 
        t.updated_at = new Date().toISOString(); 
        await idbPut('tables', t); 
      }
    }
    
    const kotNumber = this._generateKOTNumber();
    await idbAdd('kot_log', { order_id: orderId, kot_number: kotNumber, items: safeStringify(order.items), status: 'pending', printed_at: null, created_at: new Date().toISOString() });
    const allItems = await idbGetAll('order_items');
    for (const it of allItems.filter(x => String(x.order_id) === String(orderId))) {
      it.kot_number = kotNumber;
      await idbPut('order_items', it);
    }
    return { orderId, kotNumber, totalAmount, finalAmount: finalTotal };
  }

  async getOrders(filters = {}) {
    if (this.mode === 'electron') {
      let query = `SELECT o.*, t.table_number FROM orders o LEFT JOIN tables t ON o.table_id = t.id WHERE 1=1`;
      const params = [];
      if (filters.status) { query += ' AND o.status = ?'; params.push(filters.status); }
      if (filters.date) { query += ' AND DATE(o.created_at) = ?'; params.push(filters.date); }
      if (filters.table_id) { query += ' AND o.table_id = ?'; params.push(filters.table_id); }
      query += ' ORDER BY o.created_at DESC';
      return this.electronQuery(query, params);
    }
    let rows = await idbGetAll('orders');
    if (filters.status) rows = rows.filter(o => o.status === filters.status);
    if (filters.date) {
      const target = filters.date;
      rows = rows.filter(o => (o.created_at || '').startsWith(target));
    }
    if (filters.table_id) rows = rows.filter(o => String(o.table_id) === String(filters.table_id));
    const tables = await idbGetAll('tables');
    rows.forEach(o => {
      const t = tables.find(x => String(x.id) === String(o.table_id));
      o.table_number = t?.table_number || '';
    });
    return rows.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }

  async getOrderById(id) {
    if (id === undefined || id === null || id === '') {
      console.warn('[Storage] getOrderById called with invalid ID:', id);
      return null;
    }
    
    if (this.mode === 'electron') {
      const order = await this.electronQuery(`SELECT o.*, t.table_number FROM orders o LEFT JOIN tables t ON o.table_id = t.id WHERE o.id = ?`, [id]);
      if (order && order[0]) {
        order[0].items = await this.electronQuery(`SELECT oi.*, mi.name as menu_name, mi.kitchen_station, d.name as deal_name_from_deals FROM order_items oi LEFT JOIN menu_items mi ON oi.menu_item_id = mi.id LEFT JOIN deals d ON oi.deal_id = d.id WHERE oi.order_id = ?`, [id]);
        return order[0];
      }
      return null;
    }
    
    const order = await idbGetById('orders', id);
    if (order) {
      const allItems = await idbGetAll('order_items');
      const items = allItems.filter(i => String(i.order_id) === String(id));
      
      const [menuItems, deals] = await Promise.all([idbGetAll('menu_items'), idbGetAll('deals')]);
      
      for (const it of items) {
        const mi = menuItems.find(m => String(m.id) === String(it.menu_item_id));
        const d = deals.find(x => String(x.id) === String(it.deal_id));
        it.menu_name = mi?.name || '';
        it.kitchen_station = mi?.kitchen_station || '';
        it.deal_name_from_deals = d?.name || '';
        if (typeof it.modifiers === 'string') {
          try { it.modifiers = JSON.parse(it.modifiers); } catch (e) { it.modifiers = []; }
        }
      }
      order.items = items;
      
      if (order.table_id) {
        const t = await idbGetById('tables', order.table_id);
        order.table_number = t?.table_number || '';
      } else {
        order.table_number = 'N/A';
      }
    }
    return order;
  }

  async updateOrderStatus(id, status) {
    if (this.mode === 'electron') {
      const order = await this.electronQuery('SELECT table_id, total_amount FROM orders WHERE id = ?', [id]);
      const o = order[0];
      let updateQuery = 'UPDATE orders SET status = ?';
      const params = [status];
      if (status === 'billed') {
        updateQuery += ', billed_at = CURRENT_TIMESTAMP';
        const settings = await this.electronQuery('SELECT * FROM settings WHERE id = 1');
        const s = settings[0] || {};
        const tax = (o.total_amount * (s.tax_percent || 0)) / 100;
        const serviceCharge = (o.total_amount * (s.service_charge_percent || 0)) / 100;
        const finalAmount = o.total_amount + tax + serviceCharge;
        updateQuery += ', tax_amount = ?, service_charge = ?, final_amount = ?';
        params.push(tax, serviceCharge, finalAmount);
      }
      updateQuery += ' WHERE id = ?';
      params.push(id);
      const result = await this.electronQuery(updateQuery, params);
      if (SYNC_ENABLED) {
        syncToCloud('orders', { id, status, updated_at: new Date().toISOString() }, 'push', getShopId());
      }
      if (o.table_id) {
        const tableStatus = status === 'billed' ? 'cleaning' : (status === 'served' ? 'occupied' : status);
        await this.electronQuery('UPDATE tables SET status = ? WHERE id = ?', [tableStatus, o.table_id]);
        if (SYNC_ENABLED) {
          syncToCloud('tables', { id: o.table_id, status: tableStatus, updated_at: new Date().toISOString() }, 'push', getShopId());
        }
      }
      return { success: true };
    }
    const order = await idbGetById('orders', id);
    if (!order) return { success: false };
    order.status = status;
    order.updated_at = new Date().toISOString();
    if (status === 'billed') {
      order.billed_at = new Date().toISOString();
      const settings = await idbGetById('settings', '1');
      const s = settings || {};
      const tax = (order.total_amount * (s.tax_percent || 0)) / 100;
      const serviceCharge = (order.total_amount * (s.service_charge_percent || 0)) / 100;
      order.tax_amount = tax;
      order.service_charge = serviceCharge;
      order.final_amount = order.total_amount + tax + serviceCharge;
    }
    await idbPut('orders', order);
    if (order.table_id) {
      const t = await idbGetById('tables', order.table_id);
      if (t) {
        t.status = status === 'billed' ? 'cleaning' : (status === 'served' ? 'occupied' : status);
        t.updated_at = new Date().toISOString();
        if (t.status === 'cleaning' || t.status === 'empty') t.current_order_id = null;
        await idbPut('tables', t);
      }
    }
    return { success: true };
  }

  async updateItemKitchenStatus(itemId, status) {
    if (this.mode === 'electron') {
      await this.electronQuery('UPDATE order_items SET kitchen_status = ? WHERE id = ?', [status, itemId]);
      if (SYNC_ENABLED) {
        syncToCloud('order_items', { id: itemId, kitchen_status: status }, 'push', getShopId());
      }
      const item = await this.electronQuery('SELECT order_id FROM order_items WHERE id = ?', [itemId]);
      const pendingItems = await this.electronQuery(`SELECT COUNT(*) as count FROM order_items WHERE order_id = ? AND kitchen_status != 'ready'`, [item[0].order_id]);
      if (pendingItems[0].count === 0) {
        await this.electronQuery("UPDATE orders SET status = 'ready' WHERE id = ?", [item[0].order_id]);
        if (SYNC_ENABLED) {
          syncToCloud('orders', { id: item[0].order_id, status: 'ready' }, 'push', getShopId());
        }
      }
      return { success: true };
    }
    const item = await idbGetById('order_items', itemId);
    if (!item) return { success: false };
    item.kitchen_status = status;
    await idbPut('order_items', item);
    const allItems = await idbGetAll('order_items');
    const orderItems = allItems.filter(x => String(x.order_id) === String(item.order_id));
    const pending = orderItems.filter(x => x.kitchen_status !== 'ready');
    if (pending.length === 0) {
      const order = await idbGetById('orders', item.order_id);
      if (order) { order.status = 'ready'; await idbPut('orders', order); }
    }
    return { success: true };
  }

  async getPendingKOTs() {
    if (this.mode === 'electron') {
      return this.electronQuery(`SELECT k.*, o.table_id, t.table_number FROM kot_log k JOIN orders o ON k.order_id = o.id LEFT JOIN tables t ON o.table_id = t.id WHERE k.status = 'pending' ORDER BY k.created_at ASC`);
    }
    const [kots, orders, tables] = await Promise.all([idbGetAll('kot_log'), idbGetAll('orders'), idbGetAll('tables')]);
    const pending = kots.filter(k => k.status === 'pending');
    pending.forEach(k => {
      const o = orders.find(x => String(x.id) === String(k.order_id));
      const t = tables.find(x => String(x.id) === String(o?.table_id));
      k.table_id = o?.table_id || null;
      k.table_number = t?.table_number || '';
    });
    return pending.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
  }

  async splitBill(orderId, splits) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery('UPDATE orders SET split_bill_data = ? WHERE id = ?', [safeStringify(splits), orderId]);
      if (SYNC_ENABLED) {
        syncToCloud('orders', { id: orderId, split_bill_data: safeStringify(splits) }, 'push', getShopId());
      }
      return result;
    }
    const order = await idbGetById('orders', orderId);
    if (!order) return { success: false };
    order.split_bill_data = safeStringify(splits);
    await idbPut('orders', order);
    return { success: true };
  }

  async deleteOrder(orderId) {
    if (!orderId) {
      console.warn('[Storage] deleteOrder called with invalid ID:', orderId);
      return { success: false, message: 'Invalid order ID' };
    }

    if (this.mode === 'electron') {
      try {
        const order = await this.electronQuery('SELECT table_id FROM orders WHERE id = ?', [orderId]);
        
        await this.electronQuery('DELETE FROM order_items WHERE order_id = ?', [orderId]);
        await this.electronQuery('DELETE FROM kot_log WHERE order_id = ?', [orderId]);
        const result = await this.electronQuery('DELETE FROM orders WHERE id = ?', [orderId]);
        
        if (order && order[0] && order[0].table_id) {
          await this.electronQuery(
            'UPDATE tables SET status = ?, current_order_id = NULL WHERE id = ?',
            ['empty', order[0].table_id]
          );
        }
        
        if (SYNC_ENABLED) {
          syncToCloud('orders', orderId, 'remove', getShopId());
          syncToCloud('order_items', orderId, 'remove', getShopId());
          syncToCloud('kot_log', orderId, 'remove', getShopId());
        }
        return { success: true, changes: result.changes };
      } catch (err) {
        console.error('[Storage] Delete order error:', err);
        return { success: false, message: err.message };
      }
    }
    
    try {
      const order = await idbGetById('orders', orderId);
      if (!order) return { success: false, message: 'Order not found' };
      
      const allItems = await idbGetAll('order_items');
      const orderItems = allItems.filter(i => String(i.order_id) === String(orderId));
      for (const item of orderItems) {
        await idbDelete('order_items', item.id);
      }
      
      const allKots = await idbGetAll('kot_log');
      const orderKots = allKots.filter(k => String(k.order_id) === String(orderId));
      for (const kot of orderKots) {
        await idbDelete('kot_log', kot.id);
      }
      
      const result = await idbDelete('orders', orderId);
      
      if (order.table_id) {
        const table = await idbGetById('tables', order.table_id);
        if (table && table.current_order_id === orderId) {
          table.status = 'empty';
          table.current_order_id = null;
          table.updated_at = new Date().toISOString();
          await idbPut('tables', table);
        }
      }
      
      return { success: true, changes: result.changes };
    } catch (err) {
      console.error('[Storage] Delete order error:', err);
      return { success: false, message: err.message };
    }
  }

  // ==================== INVENTORY FUNCTIONS ====================
  async getInventory() {
    if (this.mode === 'electron') {
      return this.electronQuery('SELECT * FROM inventory WHERE is_active = 1 ORDER BY name');
    }
    const rows = await idbGetAll('inventory');
    return rows.filter(x => x.is_active !== 0).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }

  async addInventoryItem(item) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery(`INSERT INTO inventory (name, unit, current_stock, min_stock, max_stock, reorder_point, cost_per_unit, supplier, category) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [item.name, item.unit, item.current_stock || 0, item.min_stock || 10, item.max_stock || 100, item.reorder_point || 20, item.cost_per_unit || 0, item.supplier, item.category || 'general']);
      if (SYNC_ENABLED) {
        syncToCloud('inventory', { ...item, id: result.lastInsertRowid, is_active: 1, current_stock: item.current_stock || 0, min_stock: item.min_stock || 10, max_stock: item.max_stock || 100, reorder_point: item.reorder_point || 20, cost_per_unit: item.cost_per_unit || 0, supplier: item.supplier || '', category: item.category || 'general', created_at: new Date().toISOString() }, 'push', getShopId());
      }
      return result;
    }
    return idbAdd('inventory', {
      ...item,
      current_stock: item.current_stock || 0,
      min_stock: item.min_stock || 10,
      max_stock: item.max_stock || 100,
      reorder_point: item.reorder_point || 20,
      cost_per_unit: item.cost_per_unit || 0,
      supplier: item.supplier || '',
      category: item.category || 'general',
      is_active: 1,
      created_at: new Date().toISOString()
    });
  }

  async updateStock(id, quantity) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery('UPDATE inventory SET current_stock = current_stock + ? WHERE id = ?', [quantity, id]);
      if (SYNC_ENABLED) {
        syncToCloud('inventory', { id, current_stock_delta: quantity }, 'push', getShopId());
      }
      return result;
    }
    const item = await idbGetById('inventory', id);
    if (!item) return { changes: 0 };
    item.current_stock = (parseFloat(item.current_stock) || 0) + parseFloat(quantity);
    return idbPut('inventory', item);
  }

  async deleteInventoryItem(id) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery('DELETE FROM inventory WHERE id = ?', [id]);
      if (SYNC_ENABLED) {
        syncToCloud('inventory', id, 'remove', getShopId());
      }
      return result;
    }
    return idbDelete('inventory', id);
  }

  async getRecipes() {
    if (this.mode === 'electron') {
      return this.electronQuery(`SELECT r.*, mi.name as menu_name, i.name as ingredient_name FROM recipes r JOIN menu_items mi ON r.menu_item_id = mi.id JOIN inventory i ON r.inventory_item_id = i.id`);
    }
    const [recipes, menuItems, inventory] = await Promise.all([idbGetAll('recipes'), idbGetAll('menu_items'), idbGetAll('inventory')]);
    return recipes.map(r => {
      const mi = menuItems.find(m => String(m.id) === String(r.menu_item_id));
      const ing = inventory.find(i => String(i.id) === String(r.inventory_item_id));
      return { ...r, menu_name: mi?.name || '', ingredient_name: ing?.name || '' };
    });
  }

  async addRecipe(recipe) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery('INSERT INTO recipes (menu_item_id, inventory_item_id, quantity_needed, unit, wastage_percent) VALUES (?, ?, ?, ?, ?)',
        [recipe.menu_item_id, recipe.inventory_item_id, recipe.quantity_needed, recipe.unit, recipe.wastage_percent || 0]);
      if (SYNC_ENABLED) {
        syncToCloud('recipes', { ...recipe, id: result.lastInsertRowid, wastage_percent: recipe.wastage_percent || 0 }, 'push', getShopId());
      }
      return result;
    }
    return idbAdd('recipes', { ...recipe, wastage_percent: recipe.wastage_percent || 0 });
  }

  async deductInventoryForOrder(orderId) {
    if (this.mode === 'electron') {
      const items = await this.electronQuery('SELECT menu_item_id, quantity FROM order_items WHERE order_id = ?', [orderId]);
      for (const item of items) {
        const recipes = await this.electronQuery('SELECT * FROM recipes WHERE menu_item_id = ?', [item.menu_item_id]);
        for (const recipe of recipes) {
          const totalNeeded = recipe.quantity_needed * item.quantity;
          await this.electronQuery('UPDATE inventory SET current_stock = current_stock - ? WHERE id = ?', [totalNeeded, recipe.inventory_item_id]);
          if (SYNC_ENABLED) {
            syncToCloud('inventory', { id: recipe.inventory_item_id, current_stock_delta: -totalNeeded }, 'push', getShopId());
          }
        }
      }
      return { success: true };
    }
    const allItems = await idbGetAll('order_items');
    const items = allItems.filter(x => String(x.order_id) === String(orderId));
    const recipes = await idbGetAll('recipes');
    const inventory = await idbGetAll('inventory');
    for (const item of items) {
      const itemRecipes = recipes.filter(r => String(r.menu_item_id) === String(item.menu_item_id));
      for (const recipe of itemRecipes) {
        const totalNeeded = (parseFloat(recipe.quantity_needed) || 0) * (parseFloat(item.quantity) || 0);
        const inv = inventory.find(i => String(i.id) === String(recipe.inventory_item_id));
        if (inv) {
          inv.current_stock = (parseFloat(inv.current_stock) || 0) - totalNeeded;
          await idbPut('inventory', inv);
        }
      }
    }
    return { success: true };
  }

  // ==================== ACCOUNTS FUNCTIONS ====================
  async getExpenses(filters = {}) {
    if (this.mode === 'electron') {
      let query = 'SELECT * FROM expenses WHERE 1=1';
      const params = [];
      if (filters.category) { query += ' AND category = ?'; params.push(filters.category); }
      if (filters.fromDate && filters.toDate) { query += ' AND date BETWEEN ? AND ?'; params.push(filters.fromDate, filters.toDate); }
      query += ' ORDER BY date DESC';
      return this.electronQuery(query, params);
    }
    let rows = await idbGetAll('expenses');
    if (filters.category) rows = rows.filter(e => e.category === filters.category);
    if (filters.fromDate && filters.toDate) {
      rows = rows.filter(e => e.date >= filters.fromDate && e.date <= filters.toDate);
    }
    return rows.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  }

  async addExpense(expense) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery(`INSERT INTO expenses (category, description, amount, date, payment_method, receipt_number, notes) VALUES (?, ?, ?, ?, ?, ?, ?)`,
        [expense.category, expense.description, expense.amount, expense.date, expense.payment_method || 'cash', expense.receipt_number, expense.notes]);
      if (SYNC_ENABLED) {
        syncToCloud('expenses', { ...expense, id: result.lastInsertRowid, payment_method: expense.payment_method || 'cash', created_at: new Date().toISOString() }, 'push', getShopId());
      }
      return result;
    }
    return idbAdd('expenses', {
      ...expense,
      payment_method: expense.payment_method || 'cash',
      created_at: new Date().toISOString()
    });
  }

  async getAccountsSummary() {
    if (this.mode === 'electron') {
      const today = new Date().toISOString().split('T')[0];
      const monthStart = today.substring(0, 7) + '-01';
      
      const todaySales = await this.electronQuery(
        "SELECT COALESCE(SUM(final_amount), 0) as total FROM orders WHERE DATE(billed_at) = ? AND (status = 'billed' OR status = 'served')", 
        [today]
      );
      const monthSales = await this.electronQuery(
        "SELECT COALESCE(SUM(final_amount), 0) as total FROM orders WHERE DATE(billed_at) >= ? AND (status = 'billed' OR status = 'served')", 
        [monthStart]
      );
      const todayExpenses = await this.electronQuery(
        "SELECT COALESCE(SUM(amount), 0) as total FROM expenses WHERE date = ?", 
        [today]
      );
      const monthExpenses = await this.electronQuery(
        "SELECT COALESCE(SUM(amount), 0) as total FROM expenses WHERE date >= ?", 
        [monthStart]
      );
      const pendingOrders = await this.electronQuery(
        "SELECT COUNT(*) as count FROM orders WHERE status IN ('pending', 'preparing', 'cooking')"
      );
      const activeTables = await this.electronQuery(
        "SELECT COUNT(*) as count FROM tables WHERE status = 'occupied'"
      );
      
      return {
        todaySales: todaySales[0]?.total || 0,
        monthSales: monthSales[0]?.total || 0,
        todayExpenses: todayExpenses[0]?.total || 0,
        monthExpenses: monthExpenses[0]?.total || 0,
        pendingOrders: pendingOrders[0]?.count || 0,
        activeTables: activeTables[0]?.count || 0
      };
    }
    
    const today = todayStr();
    const monthStart = monthStartStr();
    const orders = await idbGetAll('orders');
    const expenses = await idbGetAll('expenses');
    const tables = await idbGetAll('tables');
    
    const billed = orders.filter(o => o.status === 'billed' || o.status === 'served');
    const todaySales = billed
      .filter(o => (o.billed_at || '').startsWith(today))
      .reduce((s, o) => s + (parseFloat(o.final_amount) || 0), 0);
    const monthSales = billed
      .filter(o => (o.billed_at || '') >= monthStart)
      .reduce((s, o) => s + (parseFloat(o.final_amount) || 0), 0);
    const todayExpenses = expenses
      .filter(e => (e.date || '') === today)
      .reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
    const monthExpenses = expenses
      .filter(e => (e.date || '') >= monthStart)
      .reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
    const pendingOrders = orders.filter(o => o.status === 'pending' || o.status === 'preparing' || o.status === 'cooking').length;
    const activeTables = tables.filter(t => t.status === 'occupied').length;
    
    return { 
      todaySales, 
      monthSales, 
      todayExpenses, 
      monthExpenses, 
      pendingOrders, 
      activeTables 
    };
  }

  // ==================== STAFF FUNCTIONS ====================
  async getStaff() {
    if (this.mode === 'electron') {
      return this.electronQuery('SELECT * FROM staff ORDER BY created_at DESC');
    }
    const rows = await idbGetAll('staff');
    return rows.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }

  async getStaffById(id) {
    if (this.mode === 'electron') {
      const r = await this.electronQuery('SELECT * FROM staff WHERE id = ?', [id]);
      return r[0] || null;
    }
    return idbGetById('staff', id);
  }

  async addStaff(staff) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery(`INSERT INTO staff (name, role, phone, email, password, pin_code, is_active) VALUES (?, ?, ?, ?, ?, ?, ?)`,
        [staff.name, staff.role, staff.phone || '', staff.email || '', staff.password || '', staff.pin_code, staff.is_active ? 1 : 0]);
      if (SYNC_ENABLED) {
        syncToCloud('staff', { ...staff, id: result.lastInsertRowid, phone: staff.phone || '', email: staff.email || '', password: staff.password || '', pin_code: staff.pin_code || '', is_active: staff.is_active !== undefined ? (staff.is_active ? 1 : 0) : 1, created_at: new Date().toISOString() }, 'push', getShopId());
      }
      return result;
    }
    return idbAdd('staff', {
      ...staff,
      phone: staff.phone || '',
      email: staff.email || '',
      password: staff.password || '',
      pin_code: staff.pin_code || '',
      is_active: staff.is_active !== undefined ? (staff.is_active ? 1 : 0) : 1,
      created_at: new Date().toISOString()
    });
  }

  async updateStaff(id, staff) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery(`UPDATE staff SET name = ?, role = ?, phone = ?, email = ?, password = ?, pin_code = ?, is_active = ? WHERE id = ?`,
        [staff.name, staff.role, staff.phone || '', staff.email || '', staff.password || '', staff.pin_code, staff.is_active ? 1 : 0, id]);
      if (SYNC_ENABLED) {
        syncToCloud('staff', { ...staff, id }, 'push', getShopId());
      }
      return result;
    }
    const e = await idbGetById('staff', id);
    if (!e) return { changes: 0 };
    return idbPut('staff', { ...e, ...staff, is_active: staff.is_active !== undefined ? (staff.is_active ? 1 : 0) : e.is_active, id: e.id });
  }

  async deleteStaff(id) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery('DELETE FROM staff WHERE id = ?', [id]);
      if (SYNC_ENABLED) {
        syncToCloud('staff', id, 'remove', getShopId());
      }
      return result;
    }
    return idbDelete('staff', id);
  }

  async validateStaffPin(pinCode) {
    if (this.mode === 'electron') {
      const r = await this.electronQuery('SELECT * FROM staff WHERE pin_code = ? AND is_active = 1', [pinCode]);
      return r[0] || null;
    }
    const rows = await idbGetAll('staff');
    return rows.find(s => s.pin_code === pinCode && s.is_active !== 0) || null;
  }

  // ==================== ATTENDANCE FUNCTIONS ====================
  async addAttendance(record) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery(`INSERT INTO attendance (staff_id, date, check_in, check_out, status, overtime, notes) VALUES (?, ?, ?, ?, ?, ?, ?)`,
        [record.staff_id, record.date, record.check_in, record.check_out, record.status, record.overtime || 0, record.notes]);
      if (SYNC_ENABLED) {
        syncToCloud('attendance', { ...record, id: result.lastInsertRowid, overtime: record.overtime || 0, created_at: new Date().toISOString() }, 'push', getShopId());
      }
      return result;
    }
    return idbAdd('attendance', { ...record, overtime: record.overtime || 0, created_at: new Date().toISOString() });
  }

  async getAttendance(filters = {}) {
    if (this.mode === 'electron') {
      let query = 'SELECT * FROM attendance WHERE 1=1';
      const params = [];
      if (filters.staff_id) { query += ' AND staff_id = ?'; params.push(filters.staff_id); }
      if (filters.date) { query += ' AND date = ?'; params.push(filters.date); }
      if (filters.from && filters.to) { query += ' AND date BETWEEN ? AND ?'; params.push(filters.from, filters.to); }
      query += ' ORDER BY date DESC';
      return this.electronQuery(query, params);
    }
    let rows = await idbGetAll('attendance');
    if (filters.staff_id) rows = rows.filter(r => String(r.staff_id) === String(filters.staff_id));
    if (filters.date) rows = rows.filter(r => r.date === filters.date);
    if (filters.from && filters.to) rows = rows.filter(r => r.date >= filters.from && r.date <= filters.to);
    return rows.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  }

  // ==================== SALARY FUNCTIONS ====================
  async addSalaryRecord(record) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery(`INSERT INTO salary_records (staff_id, month, year, base_salary, bonus, deductions, advance_deducted, loan_deducted, overtime_pay, net_salary, status) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'paid')`,
        [record.staff_id, record.month, record.year, record.base_salary, record.bonus || 0, record.deductions || 0, record.advance_deducted || 0, record.loan_deducted || 0, record.overtime_pay || 0, record.net_salary]);
      if (SYNC_ENABLED) {
        syncToCloud('salary_records', { ...record, id: result.lastInsertRowid, status: 'paid', created_at: new Date().toISOString() }, 'push', getShopId());
      }
      return result;
    }
    return idbAdd('salary_records', { ...record, status: 'paid', created_at: new Date().toISOString() });
  }

  async getSalaryRecords(filters = {}) {
    if (this.mode === 'electron') {
      let query = 'SELECT * FROM salary_records WHERE 1=1';
      const params = [];
      if (filters.staff_id) { query += ' AND staff_id = ?'; params.push(filters.staff_id); }
      if (filters.month) { query += ' AND month = ?'; params.push(filters.month); }
      if (filters.year) { query += ' AND year = ?'; params.push(filters.year); }
      query += ' ORDER BY year DESC, month DESC';
      return this.electronQuery(query, params);
    }
    let rows = await idbGetAll('salary_records');
    if (filters.staff_id) rows = rows.filter(r => String(r.staff_id) === String(filters.staff_id));
    if (filters.month) rows = rows.filter(r => r.month === filters.month);
    if (filters.year) rows = rows.filter(r => r.year === filters.year);
    return rows.sort((a, b) => (b.year || 0) - (a.year || 0) || (b.month || 0) - (a.month || 0));
  }

  // ==================== LOAN FUNCTIONS ====================
  async addLoan(loan) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery(`INSERT INTO loans (staff_id, type, amount, remaining, date, reason, installments, monthly_deduction, status) VALUES (?, ?, ?, ?, ?, ?, ?, ?, 'active')`,
        [loan.staff_id, loan.type, loan.amount, loan.amount, loan.date, loan.reason, loan.installments, loan.monthly_deduction]);
      if (SYNC_ENABLED) {
        syncToCloud('loans', { ...loan, id: result.lastInsertRowid, remaining: loan.amount, status: 'active', created_at: new Date().toISOString() }, 'push', getShopId());
      }
      return result;
    }
    return idbAdd('loans', { ...loan, remaining: loan.amount, status: 'active', created_at: new Date().toISOString() });
  }

  async getLoans(filters = {}) {
    if (this.mode === 'electron') {
      let query = 'SELECT * FROM loans WHERE 1=1';
      const params = [];
      if (filters.staff_id) { query += ' AND staff_id = ?'; params.push(filters.staff_id); }
      if (filters.status) { query += ' AND status = ?'; params.push(filters.status); }
      query += ' ORDER BY created_at DESC';
      return this.electronQuery(query, params);
    }
    let rows = await idbGetAll('loans');
    if (filters.staff_id) rows = rows.filter(r => String(r.staff_id) === String(filters.staff_id));
    if (filters.status) rows = rows.filter(r => r.status === filters.status);
    return rows.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }

  async updateLoanRemaining(id, amount) {
    if (this.mode === 'electron') {
      const loan = await this.electronQuery('SELECT * FROM loans WHERE id = ?', [id]);
      const l = loan[0];
      const newRemaining = (l.remaining || 0) - amount;
      const status = newRemaining <= 0 ? 'paid' : 'active';
      await this.electronQuery('UPDATE loans SET remaining = ?, status = ? WHERE id = ?', [Math.max(0, newRemaining), status, id]);
      if (SYNC_ENABLED) {
        syncToCloud('loans', { id, remaining: Math.max(0, newRemaining), status }, 'push', getShopId());
      }
      return { success: true };
    }
    const loan = await idbGetById('loans', id);
    if (!loan) return { success: false };
    const newRemaining = (parseFloat(loan.remaining) || 0) - parseFloat(amount);
    loan.remaining = Math.max(0, newRemaining);
    loan.status = loan.remaining <= 0 ? 'paid' : 'active';
    await idbPut('loans', loan);
    return { success: true };
  }

  // ==================== REPORTS FUNCTIONS ====================
  async getSalesReport(fromDate, toDate) {
    if (this.mode === 'electron') {
      return this.electronQuery(`SELECT DATE(billed_at) as date, COUNT(*) as total_orders, SUM(total_amount) as gross_sales, SUM(tax_amount) as total_tax, SUM(service_charge) as total_service_charge, SUM(discount_amount) as total_discount, SUM(final_amount) as net_sales FROM orders WHERE DATE(billed_at) BETWEEN ? AND ? AND status = 'billed' GROUP BY DATE(billed_at) ORDER BY date DESC`, [fromDate, toDate]);
    }
    const orders = await idbGetAll('orders');
    const billed = orders.filter(o => o.status === 'billed' && o.billed_at && o.billed_at >= fromDate && o.billed_at <= toDate + 'T23:59:59');
    const grouped = {};
    billed.forEach(o => {
      const date = (o.billed_at || '').split('T')[0];
      if (!grouped[date]) grouped[date] = { date, total_orders: 0, gross_sales: 0, total_tax: 0, total_service_charge: 0, total_discount: 0, net_sales: 0 };
      grouped[date].total_orders++;
      grouped[date].gross_sales += parseFloat(o.total_amount) || 0;
      grouped[date].total_tax += parseFloat(o.tax_amount) || 0;
      grouped[date].total_service_charge += parseFloat(o.service_charge) || 0;
      grouped[date].total_discount += parseFloat(o.discount_amount) || 0;
      grouped[date].net_sales += parseFloat(o.final_amount) || 0;
    });
    return Object.values(grouped).sort((a, b) => b.date.localeCompare(a.date));
  }

  async getMenuReport(fromDate, toDate) {
    if (this.mode === 'electron') {
      return this.electronQuery(`SELECT oi.item_name, SUM(oi.quantity) as total_sold, SUM(oi.unit_price * oi.quantity) as total_revenue FROM order_items oi JOIN orders o ON oi.order_id = o.id WHERE DATE(o.billed_at) BETWEEN ? AND ? AND o.status = 'billed' GROUP BY oi.item_name ORDER BY total_sold DESC`, [fromDate, toDate]);
    }
    const [orderItems, orders] = await Promise.all([idbGetAll('order_items'), idbGetAll('orders')]);
    const billedOrderIds = new Set(orders.filter(o => o.status === 'billed' && o.billed_at && o.billed_at >= fromDate && o.billed_at <= toDate + 'T23:59:59').map(o => String(o.id)));
    const items = orderItems.filter(i => billedOrderIds.has(String(i.order_id)));
    const grouped = {};
    items.forEach(i => {
      if (!grouped[i.item_name]) grouped[i.item_name] = { item_name: i.item_name, total_sold: 0, total_revenue: 0 };
      grouped[i.item_name].total_sold += parseInt(i.quantity) || 0;
      grouped[i.item_name].total_revenue += (parseFloat(i.unit_price) || 0) * (parseInt(i.quantity) || 0);
    });
    return Object.values(grouped).sort((a, b) => b.total_sold - a.total_sold);
  }

  async getStaffReport(fromDate, toDate) {
    if (this.mode === 'electron') {
      return this.electronQuery(`SELECT s.name, COUNT(o.id) as total_orders, SUM(o.final_amount) as total_sales FROM orders o JOIN staff s ON o.waiter_id = s.id WHERE DATE(o.billed_at) BETWEEN ? AND ? AND o.status = 'billed' GROUP BY s.id ORDER BY total_sales DESC`, [fromDate, toDate]);
    }
    const [orders, staff] = await Promise.all([idbGetAll('orders'), idbGetAll('staff')]);
    const billed = orders.filter(o => o.status === 'billed' && o.billed_at && o.billed_at >= fromDate && o.billed_at <= toDate + 'T23:59:59');
    const grouped = {};
    billed.forEach(o => {
      const sid = o.waiter_id;
      const s = staff.find(x => String(x.id) === String(sid));
      const name = s?.name || 'Unknown';
      if (!grouped[name]) grouped[name] = { name, total_orders: 0, total_sales: 0 };
      grouped[name].total_orders++;
      grouped[name].total_sales += parseFloat(o.final_amount) || 0;
    });
    return Object.values(grouped).sort((a, b) => b.total_sales - a.total_sales);
  }

  // ==================== SETTINGS FUNCTIONS ====================
  async getSettings() {
    if (this.mode === 'electron') {
      const r = await this.electronQuery('SELECT * FROM settings WHERE id = 1');
      return r[0] || null;
    }
    await this.init();
    return idbGetById('settings', '1');
  }

  async updateSettings(settings) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery(`UPDATE settings SET restaurant_name = ?, address = ?, phone = ?, tax_percent = ?, service_charge_percent = ?, currency_symbol = ?, printer_name = ?, kitchen_printer = ?, bill_header = ?, bill_footer = ?, theme_color = ? WHERE id = 1`,
        [settings.restaurant_name, settings.address, settings.phone, settings.tax_percent, settings.service_charge_percent, settings.currency_symbol, settings.printer_name, settings.kitchen_printer, settings.bill_header, settings.bill_footer, settings.theme_color]);
      if (SYNC_ENABLED) {
        syncToCloud('settings', { ...settings, id: '1' }, 'push', getShopId());
      }
      return result;
    }
    const s = await idbGetById('settings', '1');
    if (!s) {
      const result = await idbAdd('settings', { id: '1', ...settings, created_at: new Date().toISOString() });
      return result;
    }
    return idbPut('settings', { ...s, ...settings, id: '1' });
  }

  // ==================== USERS / AUTH FUNCTIONS ====================
  async getUsers() {
    if (this.mode === 'electron') {
      return this.electronQuery(`SELECT id, name, email, phone, role, shop_name, shop_address, business_type, currency, status, created_at FROM users WHERE is_deleted = 0 ORDER BY name`);
    }
    await this.init();
    const rows = await idbGetAll('users');
    return rows.filter(u => !u.is_deleted).map(u => {
      const { password, password_hash, ...safe } = u;
      return safe;
    }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }

  async getUserById(id) {
    if (this.mode === 'electron') {
      const r = await this.electronQuery(`SELECT id, name, email, phone, role, shop_name, shop_address, business_type, currency, status, created_at FROM users WHERE id = ? AND is_deleted = 0`, [id]);
      return r[0] || null;
    }
    const u = await idbGetById('users', id);
    if (!u || u.is_deleted) return null;
    delete u.password; delete u.password_hash;
    return u;
  }

  async getUserByEmail(email) {
    if (this.mode === 'electron') {
      const r = await this.electronQuery(`SELECT * FROM users WHERE email = ? AND is_deleted = 0 LIMIT 1`, [email]);
      return r[0] || null;
    }
    const all = await idbGetAll('users');
    return all.find(u => u.email?.toLowerCase() === email?.toLowerCase() && !u.is_deleted) || null;
  }

  async createUser(data) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery(`INSERT INTO users (name, email, phone, password, password_hash, role, shop_name, shop_address, business_type, currency, status, is_deleted, created_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, datetime('now'))`,
        [data.name, data.email, data.phone || '', data.password, data.password_hash || data.password, data.role || 'cashier', data.shop_name || '', data.shop_address || '', data.business_type || 'restaurant', data.currency || 'PKR', data.status || 'active', 0]);
      if (SYNC_ENABLED) {
        const safe = { ...data, id: result.lastInsertRowid, is_deleted: 0 };
        delete safe.password;
        delete safe.password_hash;
        syncToCloud('users', safe, 'push', getShopId());
      }
      return result;
    }
    await this.init();
    return idbAdd('users', {
      ...data,
      password_hash: data.password_hash || data.password || '',
      role: data.role || 'cashier',
      shop_name: data.shop_name || '',
      shop_address: data.shop_address || '',
      business_type: data.business_type || 'restaurant',
      currency: data.currency || 'PKR',
      status: data.status || 'active',
      is_deleted: 0,
      created_at: new Date().toISOString()
    });
  }

  async updateUser(id, data) {
    if (this.mode === 'electron') {
      const fields = []; const values = [];
      if (data.name !== undefined) { fields.push('name = ?'); values.push(data.name); }
      if (data.email !== undefined) { fields.push('email = ?'); values.push(data.email); }
      if (data.phone !== undefined) { fields.push('phone = ?'); values.push(data.phone); }
      if (data.role !== undefined) { fields.push('role = ?'); values.push(data.role); }
      if (data.shop_name !== undefined) { fields.push('shop_name = ?'); values.push(data.shop_name); }
      if (data.shop_address !== undefined) { fields.push('shop_address = ?'); values.push(data.shop_address); }
      if (data.business_type !== undefined) { fields.push('business_type = ?'); values.push(data.business_type); }
      if (data.currency !== undefined) { fields.push('currency = ?'); values.push(data.currency); }
      if (data.status !== undefined) { fields.push('status = ?'); values.push(data.status); }
      if (data.password) { fields.push('password = ?'); values.push(data.password); fields.push('password_hash = ?'); values.push(data.password_hash || data.password); }
      if (fields.length === 0) throw new Error('No fields provided for update');
      fields.push("updated_at = datetime('now')");
      values.push(id);
      const result = await this.electronQuery(`UPDATE users SET ${fields.join(', ')} WHERE id = ?`, values);
      if (SYNC_ENABLED) {
        const safe = { ...data, id };
        delete safe.password;
        delete safe.password_hash;
        syncToCloud('users', safe, 'push', getShopId());
      }
      return result;
    }
    const e = await idbGetById('users', id);
    if (!e) return { changes: 0 };
    return idbPut('users', { ...e, ...data, id: e.id, updated_at: new Date().toISOString() });
  }

  async deleteUser(id) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery("UPDATE users SET is_deleted = 1, deleted_at = datetime('now') WHERE id = ?", [id]);
      if (SYNC_ENABLED) {
        syncToCloud('users', { id, is_deleted: 1, deleted_at: new Date().toISOString() }, 'push', getShopId());
      }
      return result;
    }
    const e = await idbGetById('users', id);
    if (!e) return { changes: 0 };
    e.is_deleted = 1;
    e.deleted_at = new Date().toISOString();
    return idbPut('users', e);
  }

  async verifyUser(email, password) {
    const user = await this.getUserByEmail(email);
    if (!user) return null;
    if (user.password === password || user.password_hash === password) {
      const { password: _, password_hash: __, ...safeUser } = user;
      return safeUser;
    }
    return null;
  }

  // ==================== ROLES / PERMISSIONS FUNCTIONS ====================
  async getRoles() {
    if (this.mode === 'electron') {
      const rows = await this.electronQuery("SELECT * FROM roles WHERE is_deleted = 0 ORDER BY label");
      return rows.map(r => ({ 
        ...r, 
        pages: safeParse(r.pages), 
        permissions: safeParse(r.permissions) 
      }));
    }
    await this.init();
    const rows = await idbGetAll('roles');
    return rows
      .filter(x => x.is_deleted !== 1)
      .map(r => ({ 
        ...r, 
        pages: safeParse(r.pages), 
        permissions: safeParse(r.permissions) 
      }));
  }

  async getRoleById(id) {
    if (this.mode === 'electron') {
      const r = await this.electronQuery("SELECT * FROM roles WHERE id = ? AND is_deleted = 0", [id]);
      if (!r[0]) return null;
      return { 
        ...r[0], 
        pages: safeParse(r[0].pages), 
        permissions: safeParse(r[0].permissions) 
      };
    }
    const r = await idbGetById('roles', id);
    if (!r || r.is_deleted === 1) return null;
    return { 
      ...r, 
      pages: safeParse(r.pages), 
      permissions: safeParse(r.permissions) 
    };
  }

  async createRole(data) {
    const payload = { 
      id: data.id, 
      label: data.label, 
      color: data.color || 'primary', 
      pages: Array.isArray(data.pages) ? data.pages : [], 
      permissions: Array.isArray(data.permissions) ? data.permissions : [],
      is_deleted: 0
    };
    if (this.mode === 'electron') {
      const result = await this.electronQuery(
        `INSERT INTO roles (id, label, color, pages, permissions, is_deleted) VALUES (?, ?, ?, ?, ?, 0)`,
        [payload.id, payload.label, payload.color, safeStringify(payload.pages), safeStringify(payload.permissions)]
      );
      if (SYNC_ENABLED) {
        syncToCloud('roles', { ...payload, id: payload.id }, 'push', getShopId());
      }
      return result;
    }
    await this.init();
    return idbAdd('roles', { 
      ...payload, 
      is_deleted: 0, 
      created_at: new Date().toISOString() 
    });
  }

  async updateRole(id, data) {
    const existing = await this.getRoleById(id);
    if (!existing) return { changes: 0 };
    const updated = { 
      ...existing, 
      ...data,
      is_deleted: existing.is_deleted || 0
    };
    if (this.mode === 'electron') {
      const result = await this.electronQuery(
        `UPDATE roles SET label = ?, color = ?, pages = ?, permissions = ?, is_deleted = ? WHERE id = ?`,
        [updated.label, updated.color, safeStringify(updated.pages), safeStringify(updated.permissions), updated.is_deleted, id]
      );
      if (SYNC_ENABLED) {
        syncToCloud('roles', { ...updated, id }, 'push', getShopId());
      }
      return result;
    }
    return idbPut('roles', { 
      ...updated, 
      id: existing.id 
    });
  }

  async deleteRole(id) {
    if (id === 'admin') throw new Error('Cannot delete admin role');
    if (this.mode === 'electron') {
      const result = await this.electronQuery("UPDATE roles SET is_deleted = 1 WHERE id = ?", [id]);
      if (SYNC_ENABLED) {
        syncToCloud('roles', { id, is_deleted: 1 }, 'push', getShopId());
      }
      return result;
    }
    const e = await idbGetById('roles', id);
    if (!e) return { changes: 0 };
    e.is_deleted = 1;
    return idbPut('roles', e);
  }

  async seedDefaultRoles(defaultRoles) {
    const existing = await this.getRoles();
    if (existing.length > 0) return;
    for (const role of defaultRoles) {
      try { await this.createRole(role); } catch (e) { console.warn('Seed role error:', e); }
    }
  }

  // ==================== DEAL FUNCTIONS ====================
  async getDeals() {
    if (this.mode === 'electron') {
      const rows = await this.electronQuery('SELECT * FROM deals WHERE is_active = 1 ORDER BY sort_order, created_at DESC');
      return rows.map(deal => ({ ...deal, deal_items: safeParse(deal.deal_items) }));
    }
    await this.init();
    const rows = await idbGetAll('deals');
    return rows.filter(x => x.is_active !== 0).map(deal => ({ ...deal, deal_items: safeParse(deal.deal_items) })).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || new Date(b.created_at) - new Date(a.created_at));
  }

  async getDealById(id) {
    if (this.mode === 'electron') {
      const deal = await this.electronQuery('SELECT * FROM deals WHERE id = ? AND is_active = 1', [id]);
      if (!deal[0]) return null;
      return { ...deal[0], deal_items: safeParse(deal[0].deal_items) };
    }
    const deal = await idbGetById('deals', id);
    if (!deal || deal.is_active === 0) return null;
    return { ...deal, deal_items: safeParse(deal.deal_items) };
  }

  async addDeal(deal) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery(`INSERT INTO deals (name, name_urdu, description, price, cost_price, image, is_active, deal_items, sort_order) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [deal.name || '', deal.name_urdu || '', deal.description || '', parseFloat(deal.price) || 0, parseFloat(deal.cost_price) || 0, deal.image || null, deal.is_active !== undefined ? (deal.is_active ? 1 : 0) : 1, safeStringify(deal.deal_items), deal.sort_order || 0]);
      if (SYNC_ENABLED) {
        syncToCloud('deals', { ...deal, id: result.lastInsertRowid }, 'push', getShopId());
      }
      return result;
    }
    await this.init();
    return idbAdd('deals', {
      ...deal,
      name: deal.name || '',
      name_urdu: deal.name_urdu || '',
      description: deal.description || '',
      price: parseFloat(deal.price) || 0,
      cost_price: parseFloat(deal.cost_price) || 0,
      image: deal.image || null,
      is_active: deal.is_active !== undefined ? (deal.is_active ? 1 : 0) : 1,
      deal_items: safeStringify(deal.deal_items),
      sort_order: deal.sort_order || 0,
      created_at: new Date().toISOString()
    });
  }

  async updateDeal(id, deal) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery(`UPDATE deals SET name = ?, name_urdu = ?, description = ?, price = ?, cost_price = ?, image = ?, is_active = ?, deal_items = ?, sort_order = ? WHERE id = ?`,
        [deal.name || '', deal.name_urdu || '', deal.description || '', parseFloat(deal.price) || 0, parseFloat(deal.cost_price) || 0, deal.image || null, deal.is_active !== undefined ? (deal.is_active ? 1 : 0) : 1, safeStringify(deal.deal_items), deal.sort_order || 0, id]);
      if (SYNC_ENABLED) {
        syncToCloud('deals', { ...deal, id }, 'push', getShopId());
      }
      return result;
    }
    const e = await idbGetById('deals', id);
    if (!e) return { changes: 0 };
    return idbPut('deals', {
      ...e, ...deal,
      price: parseFloat(deal.price) || e.price || 0,
      cost_price: parseFloat(deal.cost_price) || e.cost_price || 0,
      deal_items: safeStringify(deal.deal_items || e.deal_items),
      id: e.id
    });
  }

  async deleteDeal(id) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery('UPDATE deals SET is_active = 0 WHERE id = ?', [id]);
      if (SYNC_ENABLED) {
        syncToCloud('deals', { id, is_active: 0 }, 'push', getShopId());
      }
      return result;
    }
    const e = await idbGetById('deals', id);
    if (!e) return { changes: 0 };
    e.is_active = 0;
    return idbPut('deals', e);
  }

  async hardDeleteDeal(id) {
    if (this.mode === 'electron') {
      const result = await this.electronQuery('DELETE FROM deals WHERE id = ?', [id]);
      if (SYNC_ENABLED) {
        syncToCloud('deals', id, 'remove', getShopId());
      }
      return result;
    }
    return idbDelete('deals', id);
  }

  // ==================== SYNC FROM CLOUD ====================
  async syncFromCloud() {
    console.log('[Storage] Starting cloud sync...');
    try {
      const cloudSync = await getCloudSync();
      if (!cloudSync) {
        console.warn('[Storage] Cloud sync not available');
        return;
      }
      
      // ✅ FIX: Pass shopId to syncAllFromCloud
      const cloudData = await cloudSync.syncAllFromCloud(getShopId());

      for (const [storeName, items] of Object.entries(cloudData)) {
        if (!items || items.length === 0) continue;
        try {
          await this.clearStore(storeName);
          for (const item of items) {
            if (item.id) {
              await idbPut(storeName, item);
            }
          }
          console.log(`[Storage] Synced ${items.length} items to ${storeName}`);
        } catch (e) {
          console.error(`[Storage] Failed to sync ${storeName}:`, e);
        }
      }
      console.log('[Storage] Cloud sync complete');
    } catch (error) {
      console.error('[Storage] Cloud sync failed:', error);
    }
  }

  async clearStore(storeName) {
    await ensureIndexedDB();
    const db = await initIndexedDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const r = store.clear();
      r.onsuccess = () => resolve();
      r.onerror = () => reject(r.error);
    });
  }

  async clearAllData() {
    for (const store of STORES) {
      try { await this.clearStore(store); } catch (e) {}
    }
    console.log('[Storage] All local data cleared');
  }

  // ==================== UTILITY FUNCTIONS ====================
  async exportDatabase(destinationPath) {
    if (this.mode === 'electron') {
      return { success: false, message: 'Use electron native backup in Electron mode' };
    }
    const data = {};
    for (const store of STORES) {
      data[store] = await idbGetAll(store);
    }
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `raath_dine_backup_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    return { success: true, message: 'Backup downloaded' };
  }

  async importDatabase(sourcePath) {
    if (this.mode === 'electron') {
      return { success: false, message: 'Use electron native restore in Electron mode' };
    }
    return { success: false, message: 'In browser, use JSON import via UI' };
  }

  // ==================== BACKWARD COMPATIBILITY ALIASES ====================
  async addOrder(data) { return this.createOrder(data); }
  async addRole(data) { return this.createRole(data); }
  async addUser(data) { return this.createUser(data); }
}

const db = new Storage();
export default db;